import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Space, Table } from "antd";
import { DownloadOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  downloadSavedTurnoverDetailsExcelReport,
  generateAndSaveTurnoverDetailsExcelReport,
  listTurnoverDetailsExcelReports,
} from "../../api/api";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";

type TurnoverExcelReport = {
  date: string;
  fileName: string;
  sizeBytes: number;
  createdAtMs: number;
};

export default function TurnoverExcelReportsPage() {
  const { authState } = useAuth();
  const [reports, setReports] = useState<TurnoverExcelReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingDate, setDownloadingDate] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  const isAdmin = authState.user?.role === UserRole.Admin;

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTurnoverDetailsExcelReports();
      setReports(data);
    } catch (e) {
      setError("Не удалось загрузить список отчетов");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const columns = useMemo(
    () => [
      {
        title: "Дата",
        dataIndex: "date",
        key: "date",
        width: 140,
        render: (v: string) => dayjs(v, "YYYY-MM-DD").format("DD.MM.YYYY"),
      },
      {
        title: "Размер",
        dataIndex: "sizeBytes",
        key: "sizeBytes",
        width: 120,
        render: (v: number) => `${(v / 1024 / 1024).toFixed(2)} MB`,
      },
      {
        title: "Создан",
        dataIndex: "createdAtMs",
        key: "createdAtMs",
        width: 200,
        render: (v: number) => dayjs(v).format("DD.MM.YYYY HH:mm"),
      },
      {
        title: "Действия",
        key: "actions",
        width: 160,
        render: (_: any, record: TurnoverExcelReport) => (
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={downloadingDate === record.date}
            onClick={async () => {
              setDownloadingDate(record.date);
              try {
                await downloadSavedTurnoverDetailsExcelReport(record.date);
              } finally {
                setDownloadingDate(null);
              }
            }}
          >
            Скачать
          </Button>
        ),
      },
    ],
    [downloadingDate]
  );

  if (!isAdmin) {
    return (
      <Alert
        message="Доступ запрещен"
        description="Только администратор может просматривать архив Excel отчетов по обороту."
        type="error"
        showIcon
      />
    );
  }

  return (
    <Card
      title="Архив Excel отчетов по обороту"
      extra={
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={generating}
            onClick={async () => {
              setGenerating(true);
              setError(null);
              try {
                await generateAndSaveTurnoverDetailsExcelReport();
                await fetchReports();
              } catch (e) {
                setError("Не удалось сгенерировать отчет");
              } finally {
                setGenerating(false);
              }
            }}
          >
            Сгенерировать за сегодня
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchReports}
            loading={loading}
          >
            Обновить
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert
          style={{ marginBottom: 12 }}
          message={error}
          type="error"
          showIcon
        />
      )}

      <Table
        loading={loading}
        dataSource={reports}
        columns={columns as any}
        rowKey={(r) => r.fileName}
        pagination={{ pageSize: 30, size: "small" }}
      />
    </Card>
  );
}

